// src/pages/surveys/SurveyManagementPage.tsx
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  Filter,
  MoreVertical,
  FileText,
  Copy,
  Trash2,
  Edit,
  Eye,
  Archive,
  Play,
  Pause,
  Loader2,
  ChevronDown,
  Link as LinkIcon,
  Download,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useProject } from '../../contexts/ProjectContext';
import { surveyService } from '../../services/survey.service';
import { surveyResponseService } from '../../services/surveyResponse.service';
import { Survey, SurveyStatus } from '../../types/survey.types';
import toast from 'react-hot-toast';
import { formatSafeDate } from '../../utils/dateUtils';
import * as XLSX from 'xlsx';

const STATUS_CONFIG: Record<SurveyStatus, { label: string; color: string }> = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-800' },
  active: { label: 'Active', color: 'bg-green-100 text-green-800' },
  paused: { label: 'Paused', color: 'bg-yellow-100 text-yellow-800' },
  archived: { label: 'Archived', color: 'bg-gray-100 text-gray-600' },
};

const SurveyManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  const { selectedProjectId } = useProject();
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<SurveyStatus | ''>('');
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; right: number } | null>(null);

  // Use the universal project selector (same source as workload pages)
  const currentProjectId = selectedProjectId || userProfile?.projectId || userProfile?.projects?.[0];

  // Load surveys
  useEffect(() => {
    const loadSurveys = async () => {
      if (!currentUser) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        // First try to get project-specific surveys
        let data: Survey[] = [];
        if (currentProjectId) {
          data = await surveyService.getProjectSurveys(currentProjectId);
        }

        // If no project-specific surveys, get all surveys
        if (data.length === 0) {
          data = await surveyService.getAllActiveSurveys();
        }

        setSurveys(data);
      } catch (error) {
        console.error('Error loading surveys:', error);
        toast.error('Failed to load surveys');
      } finally {
        setIsLoading(false);
      }
    };

    loadSurveys();
  }, [currentUser, currentProjectId]);

  // Filter surveys
  const filteredSurveys = surveys.filter((survey) => {
    if (searchTerm && !survey.name.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    if (statusFilter && survey.status !== statusFilter) {
      return false;
    }
    return true;
  });

  // Create new survey
  const handleCreateSurvey = () => {
    navigate('/surveys/builder/new');
  };

  // Duplicate survey
  const handleDuplicate = useCallback(async (survey: Survey) => {
    if (!currentUser || !currentProjectId || !userProfile) return;

    try {
      const duplicated = await surveyService.duplicate(
        survey.id,
        `${survey.name} (Copy)`,
        currentUser.uid,
        userProfile.name || 'User'
      );
      setSurveys((prev) => [duplicated, ...prev]);
      toast.success('Survey duplicated');
      setOpenMenuId(null);
      setMenuPosition(null);
    } catch (error) {
      console.error('Error duplicating survey:', error);
      toast.error('Failed to duplicate survey');
    }
  }, [currentUser, currentProjectId, userProfile]);

  // Update survey status
  const handleStatusChange = useCallback(async (surveyId: string, status: SurveyStatus) => {
    if (!currentUser) return;

    try {
      await surveyService.updateStatus(surveyId, status);
      setSurveys((prev) =>
        prev.map((s) => (s.id === surveyId ? { ...s, status } : s))
      );
      toast.success(`Survey ${status === 'active' ? 'activated' : status}`);
      setOpenMenuId(null);
      setMenuPosition(null);
    } catch (error) {
      console.error('Error updating survey status:', error);
      toast.error('Failed to update survey');
    }
  }, [currentUser]);

  // Delete survey
  const handleDelete = useCallback(async (surveyId: string) => {
    if (!currentUser) return;

    if (!window.confirm('Are you sure you want to delete this survey? This cannot be undone.')) {
      return;
    }

    try {
      await surveyService.delete(surveyId);
      setSurveys((prev) => prev.filter((s) => s.id !== surveyId));
      toast.success('Survey deleted');
      setOpenMenuId(null);
      setMenuPosition(null);
    } catch (error) {
      console.error('Error deleting survey:', error);
      toast.error('Failed to delete survey');
    }
  }, [currentUser]);

  // Export survey responses to Excel
  const handleExportResponses = useCallback(async (survey: Survey) => {
    try {
      toast.loading('Exporting responses...', { id: 'export' });

      const responses = await surveyResponseService.getSurveyResponses(survey.id, 10000);

      if (responses.length === 0) {
        toast.error('No responses to export', { id: 'export' });
        return;
      }

      // Build header row from questions
      const headers = ['Response ID', 'Respondent', 'Address', 'Submitted At', 'Status'];
      survey.questions.forEach(q => {
        headers.push(q.text);
      });

      // Build data rows
      const rows = responses.map(response => {
        const row: any = {
          'Response ID': response.id,
          'Respondent': response.respondentName || response.residentName || 'Anonymous',
          'Address': response.addressStreet || '',
          'Submitted At': formatSafeDate(response.completedAt, 'yyyy-MM-dd HH:mm'),
          'Status': response.status,
        };

        // Add answer for each question
        // responses is stored as Record<questionId, value>
        survey.questions.forEach(q => {
          const value = response.responses[q.id];
          if (value !== undefined && value !== null) {
            if (Array.isArray(value)) {
              row[q.text] = value.join(', ');
            } else {
              row[q.text] = String(value);
            }
          } else {
            row[q.text] = '';
          }
        });

        return row;
      });

      // Create workbook
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(rows);
      XLSX.utils.book_append_sheet(wb, ws, 'Responses');

      // Save file
      const fileName = `${survey.name.replace(/[^a-z0-9]/gi, '_')}_responses_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);

      toast.success(`Exported ${responses.length} responses`, { id: 'export' });
      setOpenMenuId(null);
      setMenuPosition(null);
    } catch (error) {
      console.error('Error exporting responses:', error);
      toast.error('Failed to export responses', { id: 'export' });
    }
  }, []);

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <FileText className="h-7 w-7 mr-3 text-indigo-600" />
            Survey Management
          </h1>
          <p className="text-gray-600 mt-1">
            Create and manage surveys for your canvassing operations
          </p>
        </div>
        <button
          onClick={handleCreateSurvey}
          className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
        >
          <Plus className="h-5 w-5 mr-2" />
          Create Survey
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[200px] max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search surveys..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as SurveyStatus | '')}
            className="border border-gray-300 rounded-md px-4 py-2"
          >
            <option value="">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="archived">Archived</option>
          </select>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        </div>
      ) : filteredSurveys.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900">No surveys found</h3>
          <p className="text-gray-500 mt-2">
            {surveys.length === 0
              ? 'Create your first survey to get started.'
              : 'Try adjusting your filters.'}
          </p>
          {surveys.length === 0 && (
            <button
              onClick={handleCreateSurvey}
              className="mt-4 inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
            >
              <Plus className="h-5 w-5 mr-2" />
              Create Survey
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Survey
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Questions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Responses
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredSurveys.map((survey) => (
                <tr key={survey.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <button
                      onClick={() => navigate(`/surveys/builder/${survey.id}`)}
                      className="text-left hover:text-indigo-600"
                    >
                      <p className="font-medium text-gray-900 hover:text-indigo-600">{survey.name}</p>
                      {survey.description && (
                        <p className="text-sm text-gray-500 truncate max-w-xs">
                          {survey.description}
                        </p>
                      )}
                    </button>
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        STATUS_CONFIG[survey.status].color
                      }`}
                    >
                      {STATUS_CONFIG[survey.status].label}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {survey.questions.length}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {/* Would need to fetch response count */}
                    --
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {formatSafeDate(survey.createdAt)}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="relative inline-block text-left">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const rect = e.currentTarget.getBoundingClientRect();
                          if (openMenuId === survey.id) {
                            setOpenMenuId(null);
                            setMenuPosition(null);
                          } else {
                            setMenuPosition({
                              top: rect.bottom + 4,
                              right: window.innerWidth - rect.right,
                            });
                            setOpenMenuId(survey.id);
                          }
                        }}
                        className="p-2 text-gray-400 hover:text-gray-600 rounded"
                      >
                        <MoreVertical className="h-5 w-5" />
                      </button>

                      {openMenuId === survey.id && menuPosition && (
                        <>
                          <div
                            className="fixed inset-0 z-[100]"
                            onClick={() => {
                              setOpenMenuId(null);
                              setMenuPosition(null);
                            }}
                          />
                          <div
                            className="fixed w-48 bg-white rounded-md shadow-lg border border-gray-200 z-[101] max-h-80 overflow-y-auto"
                            style={{
                              top: menuPosition.top,
                              right: menuPosition.right,
                            }}
                          >
                            <button
                              onClick={() => navigate(`/surveys/builder/${survey.id}`)}
                              className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            >
                              <Edit className="h-4 w-4 mr-2" />
                              Edit
                            </button>
                            <button
                              onClick={() => navigate(`/surveys/preview/${survey.id}`)}
                              className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              Preview
                            </button>
                            <button
                              onClick={() => handleDuplicate(survey)}
                              className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            >
                              <Copy className="h-4 w-4 mr-2" />
                              Duplicate
                            </button>
                            <button
                              onClick={() => handleExportResponses(survey)}
                              className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            >
                              <Download className="h-4 w-4 mr-2" />
                              Export Responses
                            </button>
                            <hr className="my-1" />
                            {survey.status === 'draft' && (
                              <button
                                onClick={() => handleStatusChange(survey.id, 'active')}
                                className="w-full flex items-center px-4 py-2 text-sm text-green-600 hover:bg-green-50"
                              >
                                <Play className="h-4 w-4 mr-2" />
                                Activate
                              </button>
                            )}
                            {survey.status === 'active' && (
                              <button
                                onClick={() => handleStatusChange(survey.id, 'paused')}
                                className="w-full flex items-center px-4 py-2 text-sm text-yellow-600 hover:bg-yellow-50"
                              >
                                <Pause className="h-4 w-4 mr-2" />
                                Pause
                              </button>
                            )}
                            {survey.status === 'paused' && (
                              <button
                                onClick={() => handleStatusChange(survey.id, 'active')}
                                className="w-full flex items-center px-4 py-2 text-sm text-green-600 hover:bg-green-50"
                              >
                                <Play className="h-4 w-4 mr-2" />
                                Resume
                              </button>
                            )}
                            {survey.status !== 'archived' && (
                              <button
                                onClick={() => handleStatusChange(survey.id, 'archived')}
                                className="w-full flex items-center px-4 py-2 text-sm text-gray-600 hover:bg-gray-50"
                              >
                                <Archive className="h-4 w-4 mr-2" />
                                Archive
                              </button>
                            )}
                            <hr className="my-1" />
                            <button
                              onClick={() => handleDelete(survey.id)}
                              className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SurveyManagementPage;
